// src/components/doctor/DoctorTriageView.jsx
//
// Inline triage report viewer for the doctor portal.
// 9 sub-tabs: Overview · Markers · Disease Risk ·
//             Trajectory · Convergence · Your Action Items · Clinical Detail ·
//             Lifestyle · Current Markers
//
// Gap 2 addition: LOW confidence banner — shown when confidenceLevel === 'LOW'
// (first draw). The Constitution §I.1: "First draw only = LOW confidence.
// Escalation above AMBER blocked. Confidence basis must be stated in every
// clinical output."
//
// Current Markers tab (11 Aug 2026): reuses EffectivePanelTab from the
// navigator portal's Current Markers tab verbatim — it is portal-agnostic
// (fetches GET /api/patients/{patientId}/effective-panel from the `patient`
// prop it's given) so nothing doctor-specific needed to be built. Unlike the
// other 7 tabs here, it is not derived from `report` — it reflects every
// draw on file, not just the one being viewed — which is why it reads
// patientAdapted directly rather than any triage-result-shaped data.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportConfig } from '../../utils/reportConfig';
import TriageReportModal, {
  OverviewTab, DeterministicMarkersTab, RiskTab, TrajectoryTab,
  ConvergenceTab, ActionPlanTab, CarePlanTab,
  injectCss, buildMarkers, buildPrintHtml, parseJson, parseList,
  parseModel3Evidence, rl, fmtDate,
} from '../healthrecords/TriageReportModal';
import EmptyState from '../EmptyState';
import { cleanActionItemText } from '../../utils/actionItems';
import ErrorBoundary from '../ErrorBoundary';
import EffectivePanelTab from '../healthrecords/EffectivePanelTab';
import PatientConfoundsTab from '../healthrecords/PatientConfoundsTab';
import Icon from '../Icon';
import LabTestSuggestionPicker from './LabTestSuggestionPicker';

// ── Adapter: TriageReportDto → TriageReportModal prop shapes ──────────────────

export const adaptReport = (report) => ({
  id:                      report.id,
  consensusRiskLevel:      report.consensusRiskLevel,
  consensusRiskScore:      report.consensusRiskScore,
  claudeRiskScore:         report.claudeRiskScore,
  rfRiskScore:             report.rfRiskScore,
  claudeKeyFindings:       report.claudeKeyFindings,
  claudeRecommendation:    report.claudeRecommendation,
  claudeMissingMarkers:    report.claudeMissingMarkers,
  claudeRawResponse:       report.claudeRawResponse,
  lifestyleRecommendation: report.lifestyleRecommendation,
  hasAcuteAlert:           report.hasAcuteAlert,
  acuteAlertDetail:        report.acuteAlertDetail,
  // Gap 2: pass confidence level through so RiskTab can use backend value
  confidenceLevel:         report.confidenceLevel,
  drawCount:               report.drawCount,
  // Gap 3: AI prohibition zone violation flag
  prohibitionZoneViolation: report.prohibitionZoneViolation,
  prohibitionZoneDetails:   report.prohibitionZoneDetails,
  // Actual blood draw date from health_records (not triage creation date)
  drawDate:                 report.drawDate,
  actionPlanJson:          report.actionPlan ? JSON.stringify(report.actionPlan) : null,
  actionPlanCheckedJson:   null,
  createdAt:               report.createdAt,
  rfRiskLevel:             report.consensusRiskLevel,
  rfConfidence:            null,
  rfTreesUsed:             null,
  rfTotalTrees:            null,
  rfFeatureFlags:          null,
  rfMissingKeys:           null,
  // Sprint 11: CRITICAL escalation SLA fields — powers EscalationSlaBadge
  // live countdown in ReportHeader. Constitution §III: 2h SLA.
  // Null when no CriticalEscalation row exists for this triage result.
  escalationStatus:        report.escalationStatus     ?? null,
  escalationNotifiedAt:    report.escalationNotifiedAt ?? null,
  // Deterministic Model (V95/V105 fields) — same TriageResultDTO.Response
  // shape the navigator portal reads; just never copied through here, so
  // the doctor portal's Deterministic Model tab was silently missing this.
  deterministicRiskScore:     report.deterministicRiskScore     ?? null,
  deterministicRiskLevel:     report.deterministicRiskLevel     ?? null,
  deterministicEvaluatedAt:   report.deterministicEvaluatedAt   ?? null,
  deterministicFeatureFlags:  report.deterministicFeatureFlags  ?? null,
  activeModels:                report.activeModels               ?? null,
  escalationModel:             report.escalationModel            ?? null,
});

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

export const adaptPatient = (patient) => ({
  id:        patient?.id,
  firstName: patient?.firstName,
  lastName:  patient?.lastName,
  age:       patient?.age,
  gender:    patient?.gender,
  bloodType: null,
  phone:     patient?.phone,
});

// ── Report header ─────────────────────────────────────────────────────────────

function ReportHeader({ report, patient, onDownload, onPatientView, showOptimal, onShowOptimalChange }) {
  const score    = report.consensusRiskScore ?? 0;
  const rs       = 7, ringSize = 72;
  const r2       = (ringSize - rs) / 2;
  const C2       = 2 * Math.PI * r2;
  const offset   = C2 * (1 - score / 100);
  const rColor   = score >= 70 ? 'var(--red)' : score >= 40 ? 'var(--rpt-warn)' : 'var(--green)';
  const reportId = `PRV-${String(patient?.id || '').replace(/-/g, '').slice(0, 8).toUpperCase()}-${new Date(report.createdAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}`;

  const ageMs    = Date.now() - new Date(report.createdAt || 0).getTime();
  const ageDays  = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  const isStale  = ageDays > 90;
  const ageLabel = ageDays === 0 ? 'Today'
    : ageDays === 1 ? 'Yesterday'
    : ageDays < 30  ? `${ageDays} days ago`
    : ageDays < 365 ? `${Math.floor(ageDays / 30)} months ago`
    : `${Math.floor(ageDays / 365)} year${Math.floor(ageDays / 365) > 1 ? 's' : ''} ago`;

  return (
    <div className="rpt-dr-header" style={{
      background:'var(--rpt-surface)', border:'1px solid var(--rpt-line)',
      borderRadius:14, padding:'16px 22px',
      display:'grid', gridTemplateColumns:'auto 1fr auto',
      gap:20, alignItems:'center', marginBottom:20,
    }}>
      <div className="rpt-dr-ring-col" style={{ display:'flex', alignItems:'center', gap:12 }}>
        <svg width={ringSize} height={ringSize} style={{ flexShrink: 0 }}>
          <circle cx={ringSize/2} cy={ringSize/2} r={r2} fill="none" stroke="var(--rpt-line-strong)" strokeWidth={rs}/>
          <circle cx={ringSize/2} cy={ringSize/2} r={r2} fill="none" stroke={rColor} strokeWidth={rs}
            strokeDasharray={C2} strokeDashoffset={offset} strokeLinecap="round"
            transform={`rotate(-90 ${ringSize/2} ${ringSize/2})`}/>
        </svg>
        <div>
          <div style={{ fontFamily:'var(--rpt-ff)', fontWeight:500, fontSize:30, lineHeight:1, color:'var(--rpt-ink)' }}>
            {score}<sup style={{ fontSize:11, color:'var(--rpt-ink-4)' }}>/100</sup>
          </div>
          <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--rpt-ink-4)', marginTop:3 }}>Risk Score</div>
          <div style={{ fontSize:11, fontWeight:600, marginTop:2, color:rColor }}>
            {String(report.consensusRiskLevel || '—').toUpperCase()}
          </div>
        </div>
      </div>

      <div className="rpt-dr-meta-col" style={{ fontSize:12, color:'var(--rpt-ink-3)', display:'flex', flexDirection:'column', gap:4, minWidth:0 }}>
        <div style={{ fontSize:11, overflowWrap:'anywhere' }}>
          Report <strong style={{ color:'var(--rpt-ink)' }}>{reportId}</strong>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
          <span>Generated <strong>{fmtDate(report.createdAt)}</strong></span>
          <span style={{
            fontSize: 11, padding: '1px 7px', borderRadius: 99,
            background: isStale ? 'var(--rpt-warn-tint)' : 'var(--rpt-good-tint)',
            color: isStale ? 'var(--amber-text)' : 'var(--green)',
            fontWeight: 500,
          }}>
            {ageLabel}
          </span>
        </div>
        {isStale && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11, color: 'var(--amber-text)',
            background: 'var(--rpt-warn-tint)', padding: '3px 8px',
            borderRadius: 6, fontWeight: 500, marginTop: 2,
          }}
            role="alert"
            aria-live="polite"
          >
            ⚠ Data may be outdated — consider re-running triage
          </div>
        )}
        {report.hasAcuteAlert && (
          <div style={{ marginTop:4, display:'inline-flex', alignItems:'center', gap:6,
            background:'var(--rpt-bad-tint)', color:'var(--red)',
            padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:600 }}>
            Acute alert — immediate review required
          </div>
        )}
        {report.escalationStatus && report.escalationStatus !== 'ACKNOWLEDGED' && (
          <EscalationSlaBadge
            status={report.escalationStatus}
            notifiedAt={report.escalationNotifiedAt}
          />
        )}
      </div>

      <div className="rpt-dr-stats-col" style={{ display:'flex', gap:18, paddingLeft:18, borderLeft:'1px solid var(--rpt-line)', alignItems:'center', flexWrap:'wrap' }}>
        {onPatientView && (
          <button
            onClick={onPatientView}
            title="See this report exactly as the patient sees it"
            style={{
              marginLeft: 8,
              background: 'transparent', color: 'var(--rpt-ink)',
              border: '1px solid var(--rpt-line-strong)', borderRadius: 7,
              padding: '7px 13px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--rpt-fs)', flexShrink: 0,
            }}
          >
            Patient view
          </button>
        )}
        {onDownload && onShowOptimalChange && (
          <label title="Include optimal markers in the PDF" style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--rpt-fs)', flexShrink: 0 }}>
            <input type="checkbox" checked={!!showOptimal} onChange={e => onShowOptimalChange(e.target.checked)} />
            Show optimal markers
          </label>
        )}
        {onDownload && (
          <button
            onClick={onDownload}
            title="Download PDF"
            style={{
              marginLeft: 8,
              background: 'var(--brand-dark)', color: 'var(--text-on-brand-dark)',
              border: 'none', borderRadius: 7,
              padding: '7px 13px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--rpt-fs)',
              display: 'flex', alignItems: 'center', gap: 5,
              flexShrink: 0,
            }}
          >
            ⬇ PDF
          </button>
        )}
      </div>
    </div>
  );
}

// ── Highlighted markers ("needs attention") picker ──────────────────────────
//
// 22 Sep 2026: lets a doctor pick 1-2 markers from this report to name the
// Health Journey milestone label on the patient's Today screen (e.g.
// "LDL flagged" instead of the generic "New finding flagged") — see backend
// HealthJourneyService for the read side and WorstMarkerResolver for the
// data-driven fallback used until a doctor has picked. canonicalCode is
// intentionally sent as null on save — that field only matters to
// WorstMarkerResolver's own resolution path, not to a doctor's explicit
// pick, which HealthJourneyService reads by markerName alone.
function HighlightedMarkersPicker({ report, markers, onSave }) {
  const current = report.highlightedMarkers || [];
  const [editing, setEditing]   = useState(false);
  const [selected, setSelected] = useState(() => current.map(m => m.markerName));
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  if (!markers || markers.length === 0) return null;

  function toggle(name) {
    setSelected(prev => {
      if (prev.includes(name)) return prev.filter(n => n !== name);
      if (prev.length >= 2) return prev; // at most 2 — see backend MAX_HIGHLIGHTED_MARKERS
      return [...prev, name];
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onSave(selected.map(name => ({ canonicalCode: null, markerName: name })));
      setEditing(false);
    } catch {
      setError("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setSelected(current.map(m => m.markerName));
    setError('');
    setEditing(false);
  }

  return (
    <div style={{
      background: 'var(--rpt-surface)', border: '1px solid var(--rpt-line)',
      borderRadius: 10, padding: '12px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--rpt-ink-4)' }}>
            Markers needing attention
          </div>
          <div style={{ fontSize: 12, color: 'var(--rpt-ink-3)', marginTop: 3 }}>
            Names the Health Journey milestone for this report. Pick up to 2 — each also suggests a retest in 8 weeks, and the navigator sees your picks. Until you pick, the worst-deviation marker is used automatically.
          </div>
        </div>
        {!editing && (
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
            {current.length ? 'Change' : 'Pick markers'}
          </button>
        )}
      </div>

      {!editing && current.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {current.map(m => (
            <span key={m.markerName} style={{
              fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
              background: 'var(--rpt-warn-tint)', color: 'var(--amber-text)',
            }}>
              {m.markerName}
            </span>
          ))}
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {markers.map(m => (
              <label key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--rpt-ink-2)' }}>
                <input
                  type="checkbox"
                  checked={selected.includes(m.name)}
                  disabled={!selected.includes(m.name) && selected.length >= 2}
                  onChange={() => toggle(m.name)}
                />
                {m.name}
                {m.status === 'bad' && (
                  <span style={{ fontSize: 11, color: 'var(--red)' }}>({m.value} {m.unit})</span>
                )}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={saving} onClick={handleCancel}>
              Cancel
            </button>
            {error && <span style={{ fontSize: 12, color: 'var(--red)' }}>{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── LOW Confidence Banner ─────────────────────────────────────────────────────
//
// The Constitution §I.1: "First draw only = LOW confidence.
// Escalation above AMBER blocked for chronic markers on LOW confidence.
// Output must include: 'First draw — recommend repeat 30-45 days.'"
//
// Shown when report.confidenceLevel === 'LOW' (backend-computed, draw_count = 1).
// Uses the same amber warning palette as the stale data banner.

function LowConfidenceBanner({ drawCount }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'var(--rpt-warn-tint)',
        border: '1px solid var(--amber)',
        borderLeft: '4px solid var(--amber)',
        borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      }}
    >
      <Icon name="alert" size={18} />
      <div>
        <div style={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '.1em', color: 'var(--amber-text)',
          fontFamily: 'var(--rpt-fs)', marginBottom: 4,
        }}>
          First draw — LOW confidence
        </div>
        <div style={{
          fontSize: 12.5, color: 'var(--rpt-ink-2)', lineHeight: 1.55,
          fontFamily: 'var(--rpt-fs)',
        }}>
          This is the patient's first lab draw on record. Clinical confidence is LOW
          for chronic marker escalations — findings require repeat confirmation before
          escalation above AMBER. A repeat draw in <strong>30–45 days</strong> is recommended.
        </div>
        <div style={{
          marginTop: 6, fontSize: 11, color: 'var(--rpt-ink-4)',
          fontFamily: 'var(--rpt-fs)', fontStyle: 'italic',
        }}>
          Ref: The Constitution §I.1 · Draw {drawCount ?? 1} of record
          · Acute alerts remain actionable regardless of confidence level
        </div>
      </div>
    </div>
  );
}

// ── AI Prohibition Zone Banner ───────────────────────────────────────────────
//
// The Constitution §I.1 + §Automation Principles — Six AI Prohibition Zones.
// Shown when backend AiProhibitionZoneFilter flagged a violation in claudeRawResponse.
// This is a doctor-only warning — not shown to patients or navigators.
// The doctor reviews and decides whether to act on or dismiss the AI output.

function ProhibitionZoneBanner({ details }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'var(--red-bg)',
        border: '1px solid var(--red)',
        borderLeft: '4px solid var(--red)',
        borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      }}
    >
      <Icon name="ban" size={18} />
      <div>
        <div style={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '.1em', color: 'var(--red)',
          fontFamily: 'var(--rpt-fs)', marginBottom: 4,
        }}>
          AI safety review required
        </div>
        <div style={{
          fontSize: 12.5, color: 'var(--rpt-ink-2)', lineHeight: 1.55,
          fontFamily: 'var(--rpt-fs)',
        }}>
          This report triggered an AI Prohibition Zone alert. The AI output may
          contain language that violates clinical safety rules — such as a diagnosis,
          medication name, or false reassurance. Review carefully before communicating
          findings to the patient.
        </div>
        {details && (
          <div style={{
            marginTop: 8, fontSize: 11, color: 'var(--red)',
            fontFamily: 'var(--rpt-fs)', fontWeight: 500,
            background: 'rgba(192,57,43,0.06)', borderRadius: 6,
            padding: '6px 10px',
          }}>
            {details}
          </div>
        )}
        <div style={{
          marginTop: 6, fontSize: 11, color: 'var(--rpt-ink-4)',
          fontFamily: 'var(--rpt-fs)', fontStyle: 'italic',
        }}>
          Ref: The Constitution §I.1 · AI Prohibition Zones 1–6 · Physician review mandatory
        </div>
      </div>
    </div>
  );
}

// ── CRITICAL Escalation SLA Badge ────────────────────────────────────────────
//
// The Constitution §III: "SLA: Acknowledge within 2 hours for CRITICAL."
// Shows a live countdown/overdue indicator on triage reports with open escalations.
// Displayed in ReportHeader when escalation status is not ACKNOWLEDGED.

function EscalationSlaBadge({ status, notifiedAt }) {
  const [label, setLabel] = React.useState('');
  const [isOverdue, setIsOverdue] = React.useState(false);

  React.useEffect(() => {
    const update = () => {
      if (!notifiedAt) {
        setLabel(status === 'ESCALATED' ? 'Escalated to Dr. Ajay' : 'Awaiting notification');
        setIsOverdue(status === 'ESCALATED');
        return;
      }
      const notified  = new Date(notifiedAt);
      const slaMs     = 2 * 60 * 60 * 1000; // 2 hours
      const deadlineMs = notified.getTime() + slaMs;
      const remaining  = deadlineMs - Date.now();

      if (remaining <= 0 || status === 'ESCALATED') {
        const overdueMins = Math.floor(Math.abs(remaining) / 60000);
        setLabel(status === 'ESCALATED'
          ? `Escalated — ${overdueMins}m overdue`
          : `SLA breached — ${overdueMins}m overdue`);
        setIsOverdue(true);
      } else {
        const hrs  = Math.floor(remaining / 3600000);
        const mins = Math.floor((remaining % 3600000) / 60000);
        setLabel(`SLA: ${hrs}h ${mins}m remaining`);
        setIsOverdue(false);
      }
    };

    update();
    const interval = setInterval(update, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [notifiedAt, status]);

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 5,
        background: isOverdue ? 'var(--rpt-bad-tint)' : 'var(--rpt-warn-tint)',
        color: isOverdue ? 'var(--red)' : 'var(--rpt-warn)',
        padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      }}
    >
      ⏱ {label}
    </div>
  );
}

// ── Previu Age Widget ─────────────────────────────────────────────────────────
//
// The Constitution §VI:
//   "Year-on-year Previu Age delta is the single most powerful subscriber
//    retention and motivation metric. Calculate and communicate at every
//    annual review."
//
// Fetches the latest Previu Age score for the patient and renders a compact
// widget showing: biological age, delta from chronological age, and YoY trend.

function PreviuAgeWidget({ patientId, apiBase }) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);

  React.useEffect(() => {
    if (!patientId) { setLoading(false); return; }
    const token = localStorage.getItem('previu_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${apiBase || ''}/previu-age/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.status === 204 ? null : r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [patientId, apiBase]);

  if (loading) return (
    <div style={{ padding:'16px', textAlign:'center', color:'var(--rpt-ink-4)', fontSize:12 }}>
      Computing Previu Age…
    </div>
  );

  if (error || !data) return (
    <div style={{
      padding: '14px 16px', background: 'var(--rpt-surface)',
      border: '1px solid var(--rpt-line)', borderRadius: 10,
      fontSize: 12, color: 'var(--rpt-ink-4)', fontStyle: 'italic',
      fontFamily: 'var(--rpt-fs)',
    }}>
      Previu Age requires minimum 2 draws. Score will appear after the next lab upload.
    </div>
  );

  const delta     = data.ageDeltaYears;
  const yoy       = data.yoyDeltaYears;
  const direction = data.yoyDeltaDirection;

  // Parse to float — API returns BigDecimal which may be full precision
  // Null-safe — BigDecimal from API; chronologicalAgeYears null if patient age not in DB
  const previuAgeVal = data.previuAgeYears != null ? parseFloat(data.previuAgeYears).toFixed(1) : '—';
  const deltaVal     = delta != null ? parseFloat(delta).toFixed(1) : null;
  const yoyVal       = yoy != null ? parseFloat(yoy).toFixed(1) : null;

  const deltaColor = deltaVal == null ? 'var(--amber)'
    : parseFloat(deltaVal) <= -2 ? 'var(--green)'
    : parseFloat(deltaVal) <= 1 ? 'var(--amber)'
    : 'var(--red)';

  const yoyColor = direction === 'IMPROVING' ? 'var(--green)'
    : direction === 'DETERIORATING' ? 'var(--red)'
    : 'var(--rpt-ink-3)';

  const yoyIcon = direction === 'IMPROVING' ? '↓' : direction === 'DETERIORATING' ? '↑' : '→';

  const dimensions = [
    { label: 'Metabolic',      val: data.metabolicAge      },
    { label: 'Cardiovascular', val: data.cardiovascularAge },
    { label: 'Organ',          val: data.organAge          },
    { label: 'Nutritional',    val: data.nutritionalAge    },
    { label: 'Lifestyle',      val: data.lifestyleAge      },
  ].filter(d => d.val != null);

  return (
    <div style={{
      background: 'var(--rpt-surface)', border: '1px solid var(--rpt-line)',
      borderRadius: 12, padding: '16px 20px',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginBottom: 14 }}>
        {/* Previu Age */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.1em', color: 'var(--rpt-ink-4)',
            fontFamily: 'var(--rpt-fs)', marginBottom: 2,
          }}>
            Previu Age
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontFamily: 'var(--rpt-ff)', fontSize: 40, fontWeight: 500,
              color: 'var(--rpt-ink)', lineHeight: 1,
            }}>
              {previuAgeVal}
            </span>
            <span style={{ fontSize: 13, color: 'var(--rpt-ink-3)', fontFamily: 'var(--rpt-fs)' }}>
              years
            </span>
          </div>
        </div>

        {/* Delta from chronological age */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.1em', color: 'var(--rpt-ink-4)',
            fontFamily: 'var(--rpt-fs)', marginBottom: 2,
          }}>
            vs actual age ({data.chronologicalAgeYears != null ? data.chronologicalAgeYears : 'N/A'})
          </div>
          <div style={{
            fontFamily: 'var(--rpt-ff)', fontSize: 22, fontWeight: 500,
            color: deltaColor, lineHeight: 1,
          }}>
            {deltaVal == null ? 'N/A' : (parseFloat(deltaVal) >= 0 ? `+${deltaVal}` : deltaVal) + ' yrs'}
          </div>
          <div style={{ fontSize: 11, color: deltaColor, fontFamily: 'var(--rpt-fs)', marginTop: 2 }}>
            {deltaVal == null ? '—' : parseFloat(deltaVal) <= -2 ? 'Biologically younger' : parseFloat(deltaVal) <= 1 ? 'On track' : 'Biologically older'}
          </div>
        </div>

        {/* YoY delta */}
        {yoy != null && (
          <div>
            <div style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.1em', color: 'var(--rpt-ink-4)',
              fontFamily: 'var(--rpt-fs)', marginBottom: 2,
            }}>
              Year-on-year
            </div>
            <div style={{
              fontFamily: 'var(--rpt-ff)', fontSize: 22, fontWeight: 500,
              color: yoyColor, lineHeight: 1,
            }}>
              {yoyIcon} {yoyVal} yrs
            </div>
            <div style={{ fontSize: 11, color: yoyColor, fontFamily: 'var(--rpt-fs)', marginTop: 2 }}>
              {direction === 'IMPROVING' ? 'Improving' : direction === 'DETERIORATING' ? 'Deteriorating' : 'Stable'}
            </div>
          </div>
        )}
      </div>

      {/* Dimension bars */}
      {dimensions.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
          {dimensions.map((dim, i) => {
            const chrono = data.chronologicalAgeYears || 35;
            const d = (dim.val - chrono).toFixed(1);
            const pct = Math.min(Math.max((parseFloat(d) + 10) / 20 * 100, 0), 100);
            const col = parseFloat(d) <= -1 ? 'var(--green)'
              : parseFloat(d) <= 2 ? 'var(--amber)' : 'var(--red)';
            return (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--rpt-ink-3)', fontFamily: 'var(--rpt-fs)' }}>
                    {dim.label}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: col, fontFamily: 'var(--rpt-ff)' }}>
                    {dim.val} yrs
                  </span>
                </div>
                <div className="rpt-bar-track">
                  <div className="rpt-bar-fill" style={{ width: `${pct}%`, background: col }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{
        marginTop: 10, fontSize: 10, color: 'var(--rpt-ink-4)',
        fontFamily: 'var(--rpt-fs)', fontStyle: 'italic',
      }}>
        Lifestyle score estimated from metabolic proxy — wearable + symptom log inputs pending.
      </div>
      <div style={{
        marginTop: 6, fontSize: 10, color: 'var(--rpt-ink-4)',
        fontFamily: 'var(--rpt-fs)',
      }}>
        Based on {data.drawsUsed} draw{data.drawsUsed !== 1 ? 's' : ''} ·
        Latest draw {data.latestDrawDate ? new Date(data.latestDrawDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'2-digit' }) : '—'} ·
        Ref: The Constitution §VI
      </div>
    </div>
  );
}

// ── Doctor Prescription section ───────────────────────────────────────────────

const PRIORITY_MAP = {
  URGENT:  { label:'Urgent',    cls:'bad',  window:'Immediate'   },
  HIGH:    { label:'This week', cls:'warn', window:'This week'   },
  MEDIUM:  { label:'Routine',   cls:'info', window:'Next visit'  },
  ROUTINE: { label:'Routine',   cls:'info', window:'Next visit'  },
};

const MAX_DESC = 200;

function DoctorPrescriptionSection({ patientId, doctorItems, onToggleItem, onAddItem }) {
  const [newDesc,     setNewDesc]     = useState('');
  const [newPriority, setNewPriority] = useState('ROUTINE');
  const [adding,      setAdding]      = useState(false);
  const [touched,     setTouched]     = useState(false);

  const pending   = doctorItems.filter(i => !i.completed);
  const completed = doctorItems.filter(i =>  i.completed);
  const charsLeft = MAX_DESC - newDesc.length;
  const isNearLimit = charsLeft <= 50;
  const isEmpty   = touched && newDesc.trim().length === 0;

  const handleAdd = async () => {
    setTouched(true);
    const trimmed = newDesc.trim();
    if (!trimmed) return;
    setAdding(true);
    await onAddItem({ description: trimmed, priority: newPriority });
    setNewDesc('');
    setTouched(false);
    setAdding(false);
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        marginBottom: 20,
      }}>
        <div style={{ flex: 1, height: 1, background: 'var(--rpt-line-strong)' }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 14px',
          background: 'var(--brand-dark)',
          borderRadius: 99,
          flexShrink: 0,
        }}>
          <Icon name="stethoscope" size={13} />
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.12em', color: 'var(--rpt-gold)',
            fontFamily: 'var(--rpt-fs)',
          }}>
            Doctor's Prescription
          </span>
        </div>
        <div style={{ flex: 1, height: 1, background: 'var(--rpt-line-strong)' }} />
      </div>

      <LabTestSuggestionPicker patientId={patientId} onAddItem={onAddItem} />

      <div style={{
        background: 'var(--rpt-surface)',
        border: '1px solid var(--rpt-line)',
        borderRadius: 11,
        padding: '14px 16px',
        marginBottom: 16,
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="Add a prescription item…"
          value={newDesc}
          maxLength={MAX_DESC}
          aria-label="Prescription item description"
          aria-describedby={isEmpty ? 'rx-error' : isNearLimit ? 'rx-counter' : undefined}
          aria-invalid={isEmpty}
          onChange={e => { setNewDesc(e.target.value); setTouched(true); }}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          onBlur={() => setTouched(true)}
          style={{
            flex: '1 1 240px', border: `1px solid ${isEmpty ? 'var(--red)' : 'var(--rpt-line-strong)'}`,
            borderRadius: 7, padding: '8px 12px', fontSize: 13,
            fontFamily: 'var(--rpt-fs)', color: 'var(--rpt-ink)',
            background: 'var(--rpt-bg)', outline: 'none',
          }}
        />
        <select
          value={newPriority}
          onChange={e => setNewPriority(e.target.value)}
          style={{
            border: '1px solid var(--rpt-line-strong)', borderRadius: 7,
            padding: '8px 10px', fontSize: 12, fontFamily: 'var(--rpt-fs)',
            color: 'var(--rpt-ink)', background: 'var(--rpt-bg)',
            cursor: 'pointer',
          }}
          aria-label="Priority"
        >
          <option value="URGENT">Urgent</option>
          <option value="HIGH">This week</option>
          <option value="ROUTINE">Routine</option>
        </select>
        <button
          onClick={handleAdd}
          disabled={adding || !newDesc.trim()}
          style={{
            background: adding || !newDesc.trim() ? 'var(--rpt-ink-5)' : 'var(--brand-dark)',
            color: 'var(--text-on-brand-dark)', border: 'none', borderRadius: 7,
            padding: '8px 18px', fontSize: 13, fontWeight: 600,
            fontFamily: 'var(--rpt-fs)', cursor: adding || !newDesc.trim() ? 'not-allowed' : 'pointer',
            transition: 'background .15s',
          }}
        >
          {adding ? '…' : 'Add'}
        </button>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', marginTop: 4, minHeight: 18 }}>
        {isEmpty ? (
          <span id="rx-error" role="alert" style={{
            fontSize: 11, color: 'var(--red)', fontFamily: 'var(--rpt-fs)',
          }}>
            Prescription item cannot be empty
          </span>
        ) : <span />}
        {isNearLimit && !isEmpty && (
          <span id="rx-counter" style={{
            fontSize: 11, fontFamily: 'var(--rpt-fs)',
            color: charsLeft <= 10 ? 'var(--red)' : 'var(--amber-text)',
            marginLeft: 'auto',
          }}>
            {charsLeft} character{charsLeft !== 1 ? 's' : ''} remaining
          </span>
        )}
      </div>

      {doctorItems.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '24px 16px',
          color: 'var(--rpt-ink-4)', fontSize: 13,
          fontFamily: 'var(--rpt-ff)', fontStyle: 'italic',
        }}>
          No prescription items yet. Add the first one above.
        </div>
      )}

      {pending.map(item => {
        const pm = PRIORITY_MAP[item.priority] || PRIORITY_MAP.ROUTINE;
        return (
          <div
            key={item.id}
            className="rpt-action"
            style={{ cursor: 'pointer', borderLeft: '3px solid var(--brand-dark)' }}
            onClick={() => onToggleItem(item.id, true)}
          >
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              border: '1.5px solid var(--rpt-line-strong)',
              background: 'var(--rpt-surface)',
              display: 'grid', placeItems: 'center',
              marginTop: 2, flexShrink: 0, cursor: 'pointer',
            }} />
            <div>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '.12em', color: 'var(--rpt-ink)',
                marginBottom: 3, fontFamily: 'var(--rpt-fs)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>Doctor</span>
                <span className={`rpt-badge ${pm.cls}`}>{pm.label}</span>
              </div>
              <div className="rpt-atitle">{cleanActionItemText(item.description)}</div>
              {item.dueDate && (
                <div style={{ fontSize: 11, color: 'var(--rpt-ink-4)', marginTop: 4 }}>
                  Due {new Date(item.dueDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'2-digit' })}
                </div>
              )}
            </div>
            <div className="rpt-awin">
              Window<strong>{pm.window}</strong>
            </div>
          </div>
        );
      })}

      {completed.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{
            fontSize: 11, color: 'var(--rpt-ink-4)', cursor: 'pointer',
            fontFamily: 'var(--rpt-fs)', userSelect: 'none',
            listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              background: 'var(--rpt-good-tint)', color: 'var(--green)',
              padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600,
            }}>
              ✓ {completed.length} completed
            </span>
            <span>— click to show</span>
          </summary>
          <div style={{ marginTop: 8, opacity: 0.6 }}>
            {completed.map(item => (
              <div
                key={item.id}
                className="rpt-action"
                style={{ cursor: 'pointer', borderLeft: '3px solid var(--green)' }}
                onClick={() => onToggleItem(item.id, false)}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  border: '1.5px solid var(--rpt-gold)',
                  background: 'var(--rpt-gold)',
                  display: 'grid', placeItems: 'center',
                  marginTop: 2, flexShrink: 0,
                }}>
                  <span style={{ color: 'var(--text-inverse)', fontSize: 12, fontWeight: 700 }}>✓</span>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '.12em', color: 'var(--green)', marginBottom: 3,
                    fontFamily: 'var(--rpt-fs)' }}>
                    Doctor · Done
                  </div>
                  <div className="rpt-atitle" style={{ textDecoration: 'line-through', color: 'var(--rpt-ink-3)' }}>
                    {cleanActionItemText(item.description)}
                  </div>
                </div>
                <div className="rpt-awin">
                  <strong style={{ fontStyle: 'normal', fontSize: 11, color: 'var(--green)' }}>
                    ✓ Done
                  </strong>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Doctor Notes tab ──────────────────────────────────────────────────────────

// authorRole (DOCTOR | NAVIGATOR | AI) is new — older notes fetched before
// the backend started tagging it fall back to DOCTOR, matching what every
// note was until now (this tab used to be doctor-only).
const NOTE_ROLE_ICON = { DOCTOR: <Icon name="stethoscope" size={13} />, NAVIGATOR: <Icon name="compass" size={13} />, AI: <Icon name="chat" size={13} /> };

function DoctorNotesTab({ notes, onAdd }) {
  const [text,   setText]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    await onAdd(trimmed);
    setText('');
    setSaving(false);
  };

  // index.css classes/tokens only — this used to be styled with the triage
  // report's --rpt-* palette, which is fixed light-theme hex, so the whole
  // composer stayed cream/white on a dark .app-shell. It's mounted by both the
  // navigator's PatientDetailPage Notes tab and the doctor's notes drawer.
  return (
    <div>
      <div className="card-title">
        Clinical Notes
        <span className="form-hint" style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
          {notes.length} note{notes.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="field-group">
        <label className="form-label" htmlFor="clinical-note">Add clinical note</label>
        <textarea
          id="clinical-note"
          className="form-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Record your clinical observation, follow-up notes, or examination findings…"
          rows={3}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span className="form-hint">Visible to care team only</span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving || !text.trim()}
          >
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>

      <div aria-live="polite" aria-relevant="additions">
      {notes.length === 0 ? (
        <div className="empty-state" style={{ padding: '32px 16px' }}>
          <div className="empty-desc">No clinical notes yet. Add the first one above.</div>
        </div>
      ) : (
        <div className="card-list">
          {notes.map(note => (
            <div key={note.id} className="panel">
              <div className="panel-head">
                <span className="field-label" style={{ marginBottom: 0 }}>
                  {NOTE_ROLE_ICON[note.authorRole] || NOTE_ROLE_ICON.DOCTOR}{' '}
                  {note.authorName || 'Doctor'}
                </span>
                <span className="card-row-meta" style={{ marginTop: 0, flexShrink: 0 }}>
                  {new Date(note.createdAt).toLocaleString('en-IN', {
                    day: 'numeric', month: 'short', year: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              </div>
              <p className="field-text" style={{ padding: '12px 16px', whiteSpace: 'pre-wrap' }}>
                {note.content}
              </p>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

// ── Combined Action Plan tab ──────────────────────────────────────────────────

function CombinedActionPlanTab({
  triageResult, allMissing, patientAdapted, healthRecord,
  doctorItems, onToggleItem, onAddItem,
}) {
  return (
    <div>
      <ActionPlanTab
        triage={triageResult}
        missing={allMissing}
        patientId={patientAdapted?.id}
        recordId={healthRecord.id}
        navigatorId={null}
        patient={patientAdapted}
        drawDate={healthRecord.drawDate}
      />
      <DoctorPrescriptionSection
        patientId={patientAdapted?.id}
        doctorItems={doctorItems}
        onToggleItem={onToggleItem}
        onAddItem={onAddItem}
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// ── Lifestyle recommendation review (doctor-portal-only gate) ────────────────
//
// Sep 2026: lifestyle recommendations became 5-6 structured points a doctor
// must accept or reject before a patient/navigator ever sees them — see
// LifestyleRecommendationService.java + DoctorPortalService.acceptLifestyleItem.
// This replaces this tab's previous use of the shared, read-only LifestyleTab
// (TriageReportModal.js) — that component still exists and is what the
// patient and navigator views render, but both of those only ever receive
// ACCEPTED points from the backend. This tab is where acceptance happens.
const STATUS_BADGE = {
  PENDING_REVIEW: { cls: 'badge-moderate', label: 'Pending review' },
  ACCEPTED:       { cls: 'badge-low',      label: '✓ Accepted' },
  REJECTED:       { cls: 'badge-unknown',  label: 'Rejected' },
};

function LifestyleReviewTab({ items, triageResultId, onAccept, onReject }) {
  const [busyId, setBusyId] = useState(null);

  const forThisReport = (items || []).filter(i => i.triageResultId === triageResultId);

  if (forThisReport.length === 0) {
    return (
      <EmptyState
        icon="stethoscope"
        title="No lifestyle recommendations yet"
        message="Personalised lifestyle points are generated automatically about 90 seconds after this triage was saved, and will appear here for review."
      />
    );
  }

  const handleAccept = async (item) => {
    setBusyId(item.id);
    try { await onAccept(item.id); } finally { setBusyId(null); }
  };
  const handleReject = async (item) => {
    setBusyId(item.id);
    try { await onReject(item.id); } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="rpt-sh" style={{ marginTop: 0, marginBottom: 14 }}>
        <div className="rpt-st">
          Lifestyle Recommendations
          <span className="a"> · {forThisReport.length} points · doctor review required</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {forThisReport.map(item => {
          const badge = STATUS_BADGE[item.status] || STATUS_BADGE.PENDING_REVIEW;
          return (
            <div key={item.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div className="field-label">
                  {item.domain}{item.timeWindow ? ` · ${item.timeWindow}` : ''}
                </div>
                <span className={`badge ${badge.cls}`}>{badge.label}</span>
              </div>
              <p className="field-text" style={{ fontWeight: 500, margin: '6px 0 0' }}>{item.habitChange}</p>
              {item.rationale && (
                <p className="field-text" style={{ margin: '4px 0 0', opacity: 0.85 }}>{item.rationale}</p>
              )}

              {item.status === 'PENDING_REVIEW' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="button" className="btn btn-primary btn-sm"
                    disabled={busyId === item.id}
                    onClick={() => handleAccept(item)}
                  >
                    {busyId === item.id ? 'Saving…' : '✓ Accept'}
                  </button>
                  <button
                    type="button" className="btn btn-ghost btn-sm"
                    disabled={busyId === item.id}
                    onClick={() => handleReject(item)}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DoctorTriageView({
  report, patient, allReports = [],
  doctorItems = [], notes = [], lifestyleItems = [],
  onToggleItem, onAddItem, onAddNote,
  onAcceptLifestyleItem, onRejectLifestyleItem,
  currentUserId, onReevaluated, onSetHighlightedMarkers,
}) {
  useEffect(() => { injectCss(); }, []);

  // ── Rules of Hooks fix (17 Jul 2026) ────────────────────────────────────
  // Both hooks below used to be declared AFTER the `if (!report) return`
  // guard further down. React requires the same hooks, in the same order,
  // on every render of a mounted component — but DoctorPatientDetailPage
  // fetches triageReports asynchronously, so the FIRST render here always
  // has report === null (early return, only useEffect ran), then a later
  // render has report populated (useEffect + useCallback + useState all
  // run). That mismatch in hook count between renders is a hard React
  // error, not a data problem — and it's exactly what the "Triage report
  // could not be displayed" ErrorBoundary fallback was catching. Moving
  // both hooks above the early return so they always run, regardless of
  // whether report is null yet.
  // 20 Jul 2026: converted from accordions to tabs, matching the navigator's
  // TriageReportModal.js exactly — same TABS shape, same reused tab
  // components, same .rpt-tabs/.rpt-tab-panel CSS (shared via injectCss()).
  // Documents and Notes were tabs #8/#9 for one round, then pulled back out
  // (UX pass, 20 Jul 2026) into icon buttons + slide-over drawers — they're
  // reference material about the PATIENT, not interpretations of THIS
  // triage result, and were competing for tab-bar space with the actual
  // clinical tabs. Back to 7, matching the navigator exactly again.
  // Previu Age folded into Overview rather than given its own tab — it's a
  // single number + trend, not enough content to justify a whole tab.
  const [tab, setTab] = useState('overview');
  const navigate = useNavigate();
  // Optimal markers are left out of the PDF unless this is ticked.
  const [showOptimal, setShowOptimal] = useState(false);
  const { clinicalDetailEnabled } = useReportConfig();

  const ALL_TABS = [
    { id: 'overview',      n: '01', label: 'Overview' },
    { id: 'deterministic', n: '02', label: 'Markers' },
    { id: 'risk',          n: '03', label: 'Disease Risk' },
    { id: 'trajectory',    n: '04', label: 'Trajectory' },
    { id: 'convergence',   n: '05', label: 'Convergence' },
    { id: 'actions',       n: '06', label: 'Your Action Items' },
    { id: 'careplan',      n: '07', label: 'Clinical Detail' },
    { id: 'lifestyle',     n: '08', label: 'Lifestyle' },
    { id: 'current',       n: '09', label: 'Current Markers' },
    { id: 'confounds',     n: '10', label: 'Confounds' },
  ];

  // handleDownload now recomputes everything it needs from the raw report/
  // patient props at click time (rather than closing over consts that used
  // to be declared post-early-return) — it only runs on click, so this is
  // cheap, and it means the hook itself no longer needs report to be
  // non-null just to be DECLARED, only to actually DO anything.
  const handleDownload = useCallback(() => {
    if (!report) return;
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) { return; }

    const patientAdapted = adaptPatient(patient);
    const triageResult   = adaptReport(report);
    const claudeResult   = parseJson(report.claudeRawResponse);
    const markers        = buildMarkers(claudeResult);
    const m3Evidence      = parseModel3Evidence(triageResult.deterministicFeatureFlags, report.claudeRawResponse);
    const missingC        = parseList(triageResult.claudeMissingMarkers);
    const missingRf       = parseList(triageResult.rfMissingKeys);
    const seen            = new Set();
    const allMissing = [...missingC, ...missingRf].filter(m => {
      const k = m.toLowerCase().trim();
      return seen.has(k) ? false : (seen.add(k), true);
    });
    const healthRecord = {
      id:       report.id,
      drawDate: report.drawDate || report.createdAt,
      labName:  report.triageType === 'BLOOD_TEST' ? 'Blood Test' : (report.triageType || 'Lab'),
      fileName: `Report · ${fmtDate(report.drawDate || report.createdAt)}`,
    };
    const triageHistory = allReports
      .filter(r => r.id !== report.id)
      .map(r => ({ ...adaptReport(r), drawDate: r.drawDate || r.createdAt }));

    const reportId = `PRV-${String(patientAdapted?.id || '').replace(/-/g, '').slice(0, 8).toUpperCase()}-${new Date(report.createdAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}`;
    const html = buildPrintHtml(reportId, patientAdapted, triageResult, markers, allMissing, healthRecord, triageHistory, m3Evidence, false, showOptimal);
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => setTimeout(() => { win.focus(); win.print(); }, 800);
  }, [report, patient, allReports, showOptimal]);

  if (!report) return (
    <EmptyState
      icon="trend"
      title="No triage report yet"
      message="A triage report will appear here once a navigator runs one from the patient's health records."
    />
  );

  const triageResult   = adaptReport(report);
  const patientAdapted = adaptPatient(patient);

  // Gap 2: LOW confidence derived from backend field.
  // Falls back to false for old records that don't have confidenceLevel set.
  const isLowConfidence = report.confidenceLevel === 'LOW';

  const claudeResult = parseJson(report.claudeRawResponse);
  const markers      = buildMarkers(claudeResult);
  const m3Evidence   = parseModel3Evidence(triageResult.deterministicFeatureFlags, report.claudeRawResponse);
  const missingC     = parseList(triageResult.claudeMissingMarkers);
  const missingRf    = parseList(triageResult.rfMissingKeys);
  const seen         = new Set();
  const allMissing   = [...missingC, ...missingRf].filter(m => {
    const k = m.toLowerCase().trim();
    return seen.has(k) ? false : (seen.add(k), true);
  });

  const triageHistory = allReports
    .filter(r => r.id !== report.id)
    .map(r => ({
      ...adaptReport(r),
      drawDate: r.drawDate || r.createdAt, // actual draw date for trajectory X-axis
    }));

  // Trajectory compares this draw with earlier ones; hide the tab when there is
  // no earlier draw, and renumber the rest so the strip never skips a number.
  const TABS = ALL_TABS
    .filter(t => t.id !== 'trajectory' || triageHistory.length > 0)
    // Clinical Detail is switched by previu.report.clinical-detail-enabled on the server (off by default).
    .filter(t => t.id !== 'careplan' || clinicalDetailEnabled)
    .map((t, idx) => ({ ...t, n: String(idx + 1).padStart(2, '0') }));

  const healthRecord = {
    id:       report.id,
    drawDate: report.drawDate || report.createdAt, // prefer actual draw date over triage creation date
    labName:  report.triageType === 'BLOOD_TEST' ? 'Blood Test' : (report.triageType || 'Lab'),
    fileName: `Report · ${fmtDate(report.drawDate || report.createdAt)}`,
  };

  const pendingCount  = doctorItems.filter(i => !i.completed).length;

  return (
    <div style={{ background:'var(--rpt-bg)', borderRadius:12, padding:'20px 24px' }}>

      <ReportHeader report={report} patient={patientAdapted} onDownload={handleDownload} showOptimal={showOptimal} onShowOptimalChange={setShowOptimal}
        onPatientView={() => navigate(`/doctor/patients/${patientAdapted.id}/reports/${report.id}`)} />

      {onSetHighlightedMarkers && (
        <HighlightedMarkersPicker
          report={report}
          markers={markers}
          onSave={(picked) => onSetHighlightedMarkers(report.id, picked)}
        />
      )}

      {/* ── LOW confidence banner — Gap 2 ──────────────────────────────────── */}
      {/* Shown before acute alert so doctor sees confidence context first.     */}
      {/* Acute alerts still actionable even on first draw per The Constitution */}
      {isLowConfidence && (
        <LowConfidenceBanner drawCount={report.drawCount} />
      )}

      {/* ── AI Prohibition Zone banner — Gap 3 ──────────────────────────────── */}
      {/* Shown when backend flagged a violation. Doctor-only. Always visible.  */}
      {report.prohibitionZoneViolation && (
        <ProhibitionZoneBanner details={report.prohibitionZoneDetails} />
      )}

      {/* ── Acute alert banner — always visible ── */}
      {triageResult.hasAcuteAlert && (
        <div className="rpt-acute" style={{ marginBottom: 16 }} role="alert">
          <div className="rpt-acute-icon"><Icon name="alert" size={20} /></div>
          <div>
            <div className="rpt-acute-title">Acute alert — immediate review required</div>
            <div className="rpt-acute-body">
              {triageResult.acuteAlertDetail || 'Critical finding detected. Review all markers immediately.'}
            </div>
          </div>
        </div>
      )}

      {/* ── Convergence alert — always visible if active ── */}
      {(() => {
        const cr = parseJson(report.claudeRawResponse);
        const conv = cr?.convergence;
        if (!conv?.active) return null;
        return (
          <div style={{
            background: 'var(--rpt-warn-tint)', border: '1px solid var(--rpt-warn)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 10,
          }} role="alert">
            <Icon name="bolt" size={18} />
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '.1em', color: 'var(--rpt-gold-deep)',
                fontFamily: 'var(--rpt-fs)', marginBottom: 2,
              }}>
                Convergence alert
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--rpt-ink-2)', lineHeight: 1.5 }}>
                {conv.summary || 'Multiple risk drivers simultaneously elevated — multi-system pattern detected.'}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Summary strip — AI assessment always visible ── */}
      {triageResult.claudeRecommendation && (
        <div className="rpt-ai" style={{ marginBottom: 20 }}>
          <div className="rpt-ai-glyph">✦</div>
          <div>
            <div className="rpt-ai-lbl">AI clinical summary</div>
            <div className="rpt-ai-title">
              {triageResult.claudeKeyFindings
                ? String(triageResult.claudeKeyFindings).replace(/\*\*/g, '').slice(0, 80)
                : 'Summary'}
            </div>
            <div className="rpt-ai-body">
              {String(triageResult.claudeRecommendation).replace(/\*\*/g, '').slice(0, 220)}
              {triageResult.claudeRecommendation?.length > 220 ? '…' : ''}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="tabs-bar rpt-tabs">
        {TABS.map(t => (
          <button
            type="button"
            key={t.id}
            className={`tab-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab panels ── */}
      <div className="rpt-tab-panel">
        {tab === 'overview' && (
          <div>
            <OverviewTab triage={triageResult} markers={markers} missing={allMissing} m3={m3Evidence} hideAcuteAlert />
            <div style={{ marginTop: 20 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '.14em', color: 'var(--rpt-ink-3)', marginBottom: 8,
                fontFamily: 'var(--rpt-fs)',
              }}>
                Previu Age
              </div>
              <PreviuAgeWidget patientId={patientAdapted?.id} apiBase={API_BASE} />
            </div>
          </div>
        )}

        {tab === 'deterministic' && (
          <DeterministicMarkersTab m3={m3Evidence} triage={triageResult} />
        )}

        {tab === 'risk' && (
          <ErrorBoundary label="Disease risk panel">
            <RiskTab triage={triageResult} markers={markers} />
          </ErrorBoundary>
        )}

        {tab === 'trajectory' && (
          <TrajectoryTab
            triage={triageResult} markers={markers}
            history={triageHistory} healthRecord={healthRecord}
          />
        )}

        {tab === 'convergence' && (
          <ConvergenceTab triage={triageResult} markers={markers} />
        )}

        {tab === 'actions' && (
          <CombinedActionPlanTab
            triageResult={triageResult}
            allMissing={allMissing}
            patientAdapted={patientAdapted}
            healthRecord={healthRecord}
            doctorItems={doctorItems}
            onToggleItem={onToggleItem}
            onAddItem={onAddItem}
          />
        )}

        {tab === 'careplan' && clinicalDetailEnabled && (
          <CarePlanTab triage={triageResult} />
        )}

        {tab === 'lifestyle' && (
          <LifestyleReviewTab
            items={lifestyleItems}
            triageResultId={report.id}
            onAccept={onAcceptLifestyleItem}
            onReject={onRejectLifestyleItem}
          />
        )}

        {tab === 'current' && (
          <ErrorBoundary label="Current markers panel">
            <EffectivePanelTab patient={patientAdapted} />
          </ErrorBoundary>
        )}

        {tab === 'confounds' && (
          <ErrorBoundary label="Confounds panel">
            <PatientConfoundsTab
              patient={patientAdapted}
              currentUserId={currentUserId}
              source="DOCTOR"
              report={{ id: healthRecord.id, drawDate: healthRecord.drawDate }}
              onReevaluated={onReevaluated}
            />
          </ErrorBoundary>
        )}
      </div>

      <div className="rpt-footer" style={{ marginTop: 24 }}>
        <span>Read-only clinical view · Data is private and secure</span>
        <span>© 2026 Previu Health · AI-assisted report</span>
      </div>
    </div>
  );
}

// ── Named export: DoctorNotesTab, reused by DoctorPatientDetailPage.jsx's
// hero-card Notes drawer (20 Jul 2026 UX pass — Documents/Notes moved out
// of this component's tab bar into icon buttons on the patient hero card,
// since they're reference material about the patient, not interpretations
// of this specific triage result). ──
export { DoctorNotesTab };

